import { tk } from '../msg/tokens';
import { RM_FIELD_KEYS } from './types';
import type {
  RemindersData,
  RmPostOpSafety,
  RmPreviewAppointment,
  RmReviewAsk,
  RmSection,
  RmStep,
  RmToken,
} from './types';

/** Tokens from alternating words and field keys. */
const t = (...parts: string[]): RmToken[] => tk(RM_FIELD_KEYS, ...parts);

let seq = 0;
const step = (s: Omit<RmStep, 'id'>): RmStep => ({ id: `rm${String(++seq)}`, ...s });

/* ------------------------------------------------------------------ *
 * 01 · Before the visit (board RM1Before).
 * ------------------------------------------------------------------ */

const BEFORE_STEPS: RmStep[] = [
  step({
    channel: 'email',
    offset: 14,
    unit: 'days',
    mode: 'template',
    on: true,
    audience: 'everyone',
    subject: t('Your appointment on ', 'day'),
    body: t(
      'Hi ',
      'first_name',
      ',\n\nThis is a reminder of your appointment with ',
      'provider',
      ' on ',
      'day',
      ' at ',
      'appointment_time',
      ' for ',
      'appointment_type',
      '.\n\n',
      'visit_instructions',
      ' We’re at ',
      'practice_address',
      '.',
    ),
    fallbacks: { first_name: 'there' },
  }),
  step({
    channel: 'text',
    offset: 3,
    unit: 'days',
    mode: 'template',
    on: true,
    audience: 'everyone',
    twoVersions: true,
    body: t('Hi ', 'first_name', ', see you ', 'day', ' at ', 'appointment_time', ' with ', 'provider', '.'),
    bodyConfirmed: t(
      'Hi ',
      'first_name',
      ', you’re all set for ',
      'day',
      ' at ',
      'appointment_time',
      ' with ',
      'provider',
      '.',
    ),
    fallbacks: { first_name: 'there' },
  }),
  step({
    // A call only ever goes to somebody who has not confirmed — there is nothing
    // to ring a confirmed patient about.
    channel: 'ai_call',
    offset: 2,
    unit: 'days',
    mode: 'smart',
    on: true,
    audience: 'not_confirmed',
    brief:
      'Ask them to confirm the appointment, or offer another time if it no longer suits. Warm, short, and never push.',
    mustInclude: ['day', 'appointment_time', 'provider'],
  }),
  step({
    channel: 'text',
    offset: 1,
    unit: 'days',
    mode: 'template',
    on: true,
    audience: 'everyone',
    twoVersions: true,
    body: t(
      'Hi ',
      'first_name',
      ', a reminder of your appointment tomorrow at ',
      'appointment_time',
      ' with ',
      'provider',
      '. ',
      'visit_instructions',
    ),
    bodyConfirmed: t(
      'Hi ',
      'first_name',
      ', thanks for confirming — just a friendly reminder that we’re expecting you tomorrow at ',
      'appointment_time',
      ' with ',
      'provider',
      '. ',
      'visit_instructions',
      ' See you then!',
    ),
    fallbacks: { first_name: 'there' },
  }),
  step({
    // Off by default. Two hours out there is nothing useful left to say.
    channel: 'text',
    offset: 2,
    unit: 'hours',
    mode: 'template',
    on: false,
    audience: 'everyone',
    body: t('See you at ', 'appointment_time', ' today — we’re at ', 'practice_address', '.'),
  }),
];

/* ------------------------------------------------------------------ *
 * 02 · After the visit — one section per procedure, plus the missed
 * follow-up (board RM0Settings card 02, RM4PostOp).
 * ------------------------------------------------------------------ */

/** A post-op text check, worded for the procedure. */
const postOpText = (offset: number, unit: 'hours' | 'days', words: string): RmStep =>
  step({
    channel: 'text',
    offset,
    unit,
    mode: 'template',
    on: true,
    audience: 'everyone',
    body: t('Hi ', 'first_name', ', ', words),
    fallbacks: { first_name: 'there' },
  });

/** The 12-hour call. Its text version is required, not optional. */
const postOpCall = (procedure: string, watchFor: string): RmStep =>
  step({
    channel: 'ai_call',
    offset: 12,
    unit: 'hours',
    mode: 'smart',
    on: true,
    audience: 'everyone',
    brief: `Ask how the ${procedure} site is doing and for a pain score from 1 to 10. Name what to watch for. Ask if there’s anything they’d like the dentist to know. Warm, calm, short.`,
    // `pain_score` and `watch_for` are locked items rather than fields — they are
    // things the call must cover, not text to merge in.
    mustInclude: ['pain_score', 'watch_for', 'safety_line'],
    textVersion: t(
      'Hi ',
      'first_name',
      ', it’s ',
      'practice_name',
      ` checking in after your ${procedure} today. How is it feeling — can you reply with a pain score from 1 to 10? Let us know if you notice ${watchFor}.`,
    ),
    fallbacks: { first_name: 'there' },
  });

function procedureSection(args: {
  key: string;
  title: string;
  steps: RmStep[];
  complications: string[];
  changedByPractice?: boolean;
}): RmSection {
  return {
    key: args.key,
    kind: 'post_op',
    title: args.title,
    titleLead: args.title,
    titleAccent: 'post-op checks',
    sub: `Calls and texts after ${/^[aeiou]/i.test(args.title) ? 'an' : 'a'} ${args.title.toLowerCase()} visit, asking how it’s going and what the dentist should know.`,
    steps: args.steps,
    maxSteps: 4,
    on: true,
    complications: args.complications,
    hasChecks: args.steps.length > 0,
    ...(args.changedByPractice === true ? { changedByPractice: true } : {}),
  };
}

const PROCEDURES: RmSection[] = [
  procedureSection({
    key: 'surgical-ext',
    title: 'Surgical extraction',
    complications: [
      'Bleeding that won’t stop',
      'Difficulty swallowing or breathing',
      'Swelling getting worse after day 3',
      'A fever',
      'Numbness after 24 hours',
    ],
    steps: [
      postOpCall('extraction', 'bleeding that won’t stop, swelling getting worse, a fever, or numbness'),
      postOpText(3, 'days', 'checking in on the extraction site — how is it healing?'),
    ],
  }),
  procedureSection({
    key: 'implant',
    title: 'Implant',
    complications: [
      'Bleeding that won’t stop',
      'Swelling getting worse after day 3',
      'A fever',
      'Numbness after 24 hours',
      'The implant feels loose',
    ],
    steps: [
      postOpCall('implant', 'bleeding that won’t stop, swelling getting worse, a fever, or numbness'),
      postOpText(3, 'days', 'checking in on your implant — how is it feeling?'),
      postOpText(10, 'days', 'one last check on your implant. Anything you’d like the dentist to know?'),
    ],
  }),
  procedureSection({
    key: 'perio',
    title: 'Perio surgery',
    complications: [
      'Bleeding that won’t stop',
      'Swelling getting worse after day 3',
      'A fever',
      'Pus or a bad taste',
    ],
    steps: [
      postOpCall('perio surgery', 'bleeding that won’t stop, swelling getting worse, a fever, or a bad taste'),
      postOpText(3, 'days', 'checking in after your perio surgery — how are the gums feeling?'),
    ],
  }),
  procedureSection({
    // The practice moved this one off the shipped default — the summary says so,
    // in amber, rather than quietly presenting it as ours.
    key: 'root-canal',
    title: 'Root canal',
    changedByPractice: true,
    complications: ['Swelling', 'Pain that keeps getting worse', 'A fever'],
    steps: [
      postOpText(24, 'hours', 'checking in after your root canal — how is the tooth feeling?'),
      postOpText(3, 'days', 'another check on that tooth. Any swelling or pain that’s getting worse?'),
    ],
  }),
  procedureSection({
    key: 'crown-seat',
    title: 'Crown seat',
    complications: ['The crown feels loose or came off', 'Pain when biting down'],
    steps: [postOpText(3, 'days', 'how is the new crown feeling — any trouble biting down?')],
  }),
  {
    // No checks, and no Edit. A procedure with nothing to check is not an error.
    key: 'hygiene-exam',
    kind: 'post_op',
    title: 'Hygiene · exam',
    titleLead: 'Hygiene · exam',
    titleAccent: 'post-op checks',
    sub: 'No checks after a cleaning or an exam.',
    steps: [],
    maxSteps: 4,
    on: false,
    complications: [],
    hasChecks: false,
  },
];

const MISSED: RmSection = {
  key: 'missed',
  kind: 'missed',
  title: 'Missed appointment',
  titleLead: 'Missed',
  titleAccent: 'appointment',
  sub: 'One text when a patient misses their visit, offering to rebook.',
  maxSteps: 1,
  on: true,
  steps: [
    step({
      channel: 'text',
      offset: 2,
      unit: 'hours',
      mode: 'template',
      on: true,
      audience: 'everyone',
      body: t(
        'Hi ',
        'first_name',
        ', we missed you at ',
        'appointment_time',
        ' today. Would you like to find another time? ',
        'change_link',
      ),
      fallbacks: { first_name: 'there' },
    }),
  ],
};

const REVIEW_SECTION: RmSection = {
  key: 'review',
  kind: 'review',
  title: 'Review ask',
  titleLead: 'The review',
  titleAccent: 'ask',
  sub: 'One text asking for a Google review, a few days after a visit.',
  maxSteps: 1,
  on: true,
  steps: [
    step({
      channel: 'text',
      offset: 5,
      unit: 'days',
      mode: 'template',
      on: true,
      audience: 'everyone',
      body: t(
        'Hi ',
        'first_name',
        ', thanks for coming in to see ',
        'provider',
        '. If you have a moment, a short review really helps other patients find us.',
      ),
      fallbacks: { first_name: 'there' },
    }),
  ],
};

const BEFORE: RmSection = {
  key: 'before',
  kind: 'before',
  title: 'Before the visit',
  titleLead: 'Before the',
  titleAccent: 'visit',
  sub: 'The messages every patient with an appointment gets. Each step can go to everyone, or depend on whether they’ve confirmed.',
  steps: BEFORE_STEPS,
  maxSteps: 6,
  on: true,
};

/* ------------------------------------------------------------------ *
 * 03 · Post-op safety, 04 · Review ask.
 * ------------------------------------------------------------------ */

const SAFETY: RmPostOpSafety = {
  painScoreAtOrAbove: 7,
  inHours: 'Transfer the call to the practice',
  outOfHours: 'Urgent task for the on-call dentist',
  neverDo: ['diagnoses', 'gives advice', 'says “that’s normal”', 'names a medication', 'decides what’s urgent'],
};

const REVIEW: RmReviewAsk = {
  on: true,
  atMostPerMonths: 12,
  afterCategories: ['Surgical', 'Restorative'],
  daysAfter: 5,
  destination: 'Google · Alamo Heights',
};

/* ------------------------------------------------------------------ *
 * The three appointments every preview is drawn from — one confirmed,
 * one not, one in another language (§3).
 * ------------------------------------------------------------------ */

const APPOINTMENTS: RmPreviewAppointment[] = [
  {
    id: 'a1',
    name: 'Marguerite Oyelowo',
    confirmed: true,
    language: 'en',
    when: 'Tomorrow 10:00 · Dr. Saito · Cleaning',
    values: {
      first_name: 'Marguerite',
      day: 'Thursday 16 October',
      appointment_date: '16 October',
      appointment_time: '10:00',
      provider: 'Dr. Saito',
      appointment_type: 'a cleaning',
      practice_name: 'Alamo Heights Dental',
      practice_address: '5500 Broadway, San Antonio',
      practice_phone: '(210) 555-0147',
      change_link: 'a link just for her',
      visit_instructions: 'Please bring your insurance card.',
    },
    rendered: {
      confirmed:
        'Hi Marguerite, thanks for confirming — just a friendly reminder that we’re expecting you tomorrow at 10:00 with Dr. Saito. Please bring your insurance card. See you then!',
      notConfirmed:
        'Hi Marguerite, a reminder of your appointment tomorrow at 10:00 with Dr. Saito. Please bring your insurance card.',
      spoken:
        'Hello, is that Marguerite? This is Alamo Heights Dental — I’m calling about your cleaning tomorrow at ten with Dr. Saito.',
    },
  },
  {
    id: 'a2',
    name: 'Devon Achebe',
    confirmed: false,
    language: 'en',
    when: 'Tomorrow 14:30 · Dr. Chen · Filling',
    values: {
      first_name: 'Devon',
      day: 'Thursday 16 October',
      appointment_date: '16 October',
      appointment_time: '14:30',
      provider: 'Dr. Chen',
      appointment_type: 'a filling',
      practice_name: 'Alamo Heights Dental',
      practice_address: '5500 Broadway, San Antonio',
      practice_phone: '(210) 555-0147',
      change_link: 'a link just for him',
      visit_instructions: 'Please arrive 10 minutes early.',
    },
    rendered: {
      notConfirmed:
        'Hi Devon, a reminder of your appointment tomorrow at 14:30 with Dr. Chen. Please arrive 10 minutes early.',
      confirmed:
        'Hi Devon, thanks for confirming — we’re expecting you tomorrow at 14:30 with Dr. Chen. Please arrive 10 minutes early.',
      spoken:
        'Hello, is that Devon? This is Alamo Heights Dental — I’m calling about your filling tomorrow at half past two with Dr. Chen. Can I confirm it for you?',
    },
  },
  {
    // Farsi, laid out right to left. Her version is the confirmed one.
    id: 'a3',
    name: 'Maryam Rezaei',
    confirmed: true,
    language: 'fa',
    when: 'Tomorrow 11:15 · Dr. Saito · Cleaning · Farsi',
    values: {
      first_name: 'Maryam',
      day: 'پنجشنبه ۱۶ اکتبر',
      appointment_date: '۱۶ اکتبر',
      appointment_time: '۱۱:۱۵',
      provider: 'دکتر سایتو',
      appointment_type: 'جرم‌گیری',
      practice_name: 'دندانپزشکی آلامو هایتس',
      practice_address: '5500 Broadway, San Antonio',
      practice_phone: '(210) 555-0147',
      change_link: 'لینک مخصوص شما',
      visit_instructions: 'لطفاً کارت بیمه‌تان را همراه داشته باشید.',
    },
    rendered: {
      confirmed:
        'سلام مریم، ممنون که نوبتتان را تأیید کردید — فقط یادآوری دوستانه که فردا ساعت ۱۱:۱۵ با دکتر سایتو منتظرتان هستیم. لطفاً کارت بیمه‌تان را همراه داشته باشید. تا فردا!',
      notConfirmed:
        'سلام مریم، یادآوری نوبت شما فردا ساعت ۱۱:۱۵ با دکتر سایتو. لطفاً کارت بیمه‌تان را همراه داشته باشید.',
    },
  },
];

export const REMINDERS: RemindersData = {
  sections: [BEFORE, MISSED, ...PROCEDURES, REVIEW_SECTION],
  safety: SAFETY,
  review: REVIEW,
  previewAppointments: APPOINTMENTS,
  appointmentsNext14Days: 214,
};

/** Two implant patients, for the post-op call previews (board RM4PostOp). */
export const POST_OP_PATIENTS: RmPreviewAppointment[] = [
  {
    id: 'p-james',
    name: 'James Harrison',
    confirmed: true,
    language: 'en',
    when: 'Implant #19 today · Dr. Okafor · English',
    values: { first_name: 'James', practice_name: 'Alamo Heights Dental', provider: 'Dr. Okafor' },
    rendered: {
      spoken:
        'Hi James, this is the automated assistant for Alamo Heights Dental, calling to check on you after your implant today. This call is recorded. How is it feeling — on a scale of 1 to 10, what’s your pain?',
    },
  },
  {
    id: 'p-parisa',
    name: 'Parisa Karimi',
    confirmed: true,
    language: 'fa',
    when: 'Implant #30 today · Dr. Okafor · Farsi',
    values: { first_name: 'پریسا', practice_name: 'دندانپزشکی آلامو هایتس', provider: 'دکتر اوکافور' },
    rendered: {
      spoken:
        'سلام پریسا، دستیار خودکار دندانپزشکی آلامو هایتس هستم و برای احوال‌پرسی بعد از ایمپلنت امروزتان تماس می‌گیرم. این تماس ضبط می‌شود. از ۱ تا ۱۰، درد شما چقدر است؟',
    },
  },
];

/** Patients with no first name on file, for the *If a detail is missing* row. */
export const RM_MISSING_COUNTS: Partial<Record<string, number>> = {
  first_name: 2,
};
